import { useEffect, useMemo, useState } from 'react';
import { Clock, Pencil, Play, Plus, Square, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { errorMessage } from '../../lib/errorMessage';
import { supabase } from '../../lib/supabase';
import { formatDuration } from './hoursMath';
import {
  useActiveTimer,
  useDeleteTimeEntry,
  useStartTimer,
  useStopTimer,
  useTimeEntries,
  type TimeEntry,
} from './hoursQueries';
import { TimeEntryDrawer } from './TimeEntryDrawer';
import styles from './hours.module.css';

interface ProjectOption { id: string; name: string }

function startOfWeekISO(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day; // Sunday = start of week
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function formatElapsed(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function HoursPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();

  const since = useMemo(() => startOfWeekISO(), []);
  const { data: entries = [], isLoading, error } = useTimeEntries(orgId, since);
  const { data: activeTimer } = useActiveTimer(orgId, userId);
  const startTimer = useStartTimer(orgId, userId);
  const stopTimer = useStopTimer(orgId, userId);
  const delEntry = useDeleteTimeEntry(orgId);

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TimeEntry | null>(null);

  // Live ticker
  useEffect(() => {
    if (!activeTimer) return;
    const interval = window.setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { window.clearInterval(interval); };
  }, [activeTimer]);
  void now;

  useEffect(() => {
    if (!orgId) return;
    const cancel = { current: false };
    void (async () => {
      const { data } = await supabase
        .from('projects')
        .select('id, name')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (cancel.current) return;
      setProjects(data ?? []);
    })();
    return () => { cancel.current = true; };
  }, [orgId]);

  const projectNameById = useMemo(() => {
    const m: Record<string, string> = {};
    projects.forEach((p) => { m[p.id] = p.name; });
    return m;
  }, [projects]);

  async function handleStart() {
    try {
      await startTimer.mutateAsync({
        project_id: projectId || null,
        description: description || null,
        billable,
      });
      setDescription('');
      toast.success('הטיימר הופעל');
    } catch (err) {
      const msg = errorMessage(err).toLowerCase().includes('unique')
        ? 'יש כבר טיימר פעיל. עצור אותו קודם.'
        : `לא הצלחנו להפעיל · ${errorMessage(err)}`;
      toast.error(msg);
    }
  }

  async function handleStop() {
    if (!activeTimer) return;
    try {
      await stopTimer.mutateAsync(activeTimer.id);
      toast.success('הטיימר נעצר');
    } catch (err) {
      toast.error(`לא הצלחנו לעצור · ${errorMessage(err)}`);
    }
  }

  async function handleDelete(entry: TimeEntry) {
    if (!window.confirm('למחוק רישום זמן?')) return;
    try {
      await delEntry.mutateAsync(entry.id);
      toast.success('נמחק');
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  const totalMinutesThisWeek = entries
    .filter((e) => e.duration_minutes !== null)
    .reduce((sum, e) => sum + (e.duration_minutes ?? 0), 0);

  const billableMinutesThisWeek = entries
    .filter((e) => e.billable && e.duration_minutes !== null)
    .reduce((sum, e) => sum + (e.duration_minutes ?? 0), 0);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1>שעות</h1>
          <p className={styles.sub}>טיימר חי, רישום שעות בדיעבד, ורישומי השבוע. שעות של פרויקט מסוים נמצאות גם בטאב השעות שלו.</p>
        </div>
        <div className={styles.headActions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            <Plus size={16} aria-hidden="true" /> הוסף שעות
          </button>
          <Clock size={32} className={styles.headIcon} aria-hidden="true" />
        </div>
      </header>

      {error ? (
        <div className={styles.errorBanner}>
          <strong>לא הצלחנו לטעון את רישומי השעות.</strong>
          <p dir="auto">{errorMessage(error)}</p>
        </div>
      ) : null}

      {/* Active timer or new timer form */}
      {activeTimer ? (
        <div className={styles.activeTimer}>
          <div>
            <div className={styles.activeBadge}>טיימר פעיל</div>
            <div className={styles.activeMain}>
              <span className={styles.elapsed}><bdi>{formatElapsed(activeTimer.started_at)}</bdi></span>
              <div className={styles.activeMeta}>
                {activeTimer.project_id ? (
                  <strong>{projectNameById[activeTimer.project_id] ?? 'פרויקט'}</strong>
                ) : <strong>ללא פרויקט</strong>}
                {activeTimer.description ? <> · {activeTimer.description}</> : null}
                {activeTimer.billable ? <span className={styles.billable}>· לחיוב</span> : null}
              </div>
            </div>
          </div>
          <button type="button" className={styles.btnDanger} onClick={() => { void handleStop(); }} disabled={stopTimer.isPending}>
            <Square size={16} aria-hidden="true" /> עצור
          </button>
        </div>
      ) : (
        <div className={styles.startCard}>
          <div className={styles.startRow}>
            <select
              className={styles.input}
              value={projectId}
              onChange={(e) => { setProjectId(e.target.value); }}
            >
              <option value="">— ללא פרויקט —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input
              type="text"
              className={styles.input}
              placeholder="מה אתה עושה? (אופציונלי)"
              value={description}
              onChange={(e) => { setDescription(e.target.value); }}
            />
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={billable}
                onChange={(e) => { setBillable(e.target.checked); }}
              />
              לחיוב
            </label>
            <button type="button" className={styles.btnPrimary} onClick={() => { void handleStart(); }} disabled={startTimer.isPending}>
              <Play size={16} aria-hidden="true" /> התחל
            </button>
          </div>
        </div>
      )}

      {/* Weekly summary */}
      <div className={styles.summaryGrid}>
        <div className={styles.summary}>
          <span className={styles.summaryLabel}>סה"כ השבוע</span>
          <span className={styles.summaryValue}><bdi>{formatDuration(totalMinutesThisWeek)}</bdi></span>
        </div>
        <div className={styles.summary}>
          <span className={styles.summaryLabel}>שעות לחיוב</span>
          <span className={styles.summaryValue}><bdi>{formatDuration(billableMinutesThisWeek)}</bdi></span>
        </div>
        <div className={styles.summary}>
          <span className={styles.summaryLabel}>רישומים</span>
          <span className={styles.summaryValue}><bdi>{String(entries.length)}</bdi></span>
        </div>
      </div>

      {/* Entries list */}
      <div className={styles.section}>
        <h3>רישומי השבוע</h3>
        {isLoading ? (
          <p className={styles.notice}>טוען...</p>
        ) : entries.length === 0 ? (
          <p className={styles.notice}>אין רישומי זמן השבוע. התחל טיימר או הוסף שעות.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>פרויקט</th>
                <th>תיאור</th>
                <th>התחיל</th>
                <th>משך</th>
                <th>חיוב</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className={e.ended_at === null ? styles.activeRow : ''}>
                  <td>{e.project_id ? (projectNameById[e.project_id] ?? '—') : <span className={styles.muted}>ללא</span>}</td>
                  <td>{e.description ?? <span className={styles.muted}>—</span>}</td>
                  <td className={styles.muted}><bdi>{format(new Date(e.started_at), 'dd/MM HH:mm', { locale: he })}</bdi></td>
                  <td>
                    {e.duration_minutes !== null ? (
                      <strong><bdi>{formatDuration(e.duration_minutes)}</bdi></strong>
                    ) : (
                      <span className={styles.runningBadge}>פעיל</span>
                    )}
                  </td>
                  <td>{e.billable ? '✓' : '—'}</td>
                  <td>
                    <div className={styles.rowActions}>
                      <button
                        type="button"
                        className={styles.btnEdit}
                        onClick={() => {
                          setEditing(e);
                          setDrawerOpen(true);
                        }}
                        aria-label="ערוך"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className={styles.btnGhost}
                        onClick={() => { void handleDelete(e); }}
                        aria-label="מחק"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TimeEntryDrawer
        open={drawerOpen}
        entry={editing}
        projects={projects}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
